import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
    DocumentRow,
    SyncFolderFailure,
    SyncReportView,
} from '../components/documents-hub/documents-hub.types';
import { DocumentDetail, DocumentInterpretationView } from '../components/document-detail/document-detail.types';
import {
    IClosingDocumentDetail,
    IClosingDocumentInterpretation,
    IClosingDocumentListItem,
    ISyncRun,
    IUploadState,
} from '../models/closing-document.model';
import { toUnits } from '../presenters/accounts-presentation';
import { CLOSING_DOCUMENT_FEATURE_KEY } from '../reducer/closing-document.feature-key';
import { ClosingDocumentState } from '../reducer/closing-document.reducer';

const selectState = createFeatureSelector<ClosingDocumentState>(CLOSING_DOCUMENT_FEATURE_KEY);

/**
 * Wire item → the hub's row. A RENAME, not a translation: the backend's response mapper is the one
 * place that knows both the persisted UPPER_SNAKE spelling and the frontend's camelCase, so nothing
 * is converted here (ADR-0009, contract B4).
 *
 * `validityHint` is deliberately not set: it is a PRE-COMPOSED tooltip ("… langfristig gültig bis
 * 31.3.2027") and nothing computes it yet — `validity` is REV-21602's to write. The view falls back
 * to its own i18n label, which is correct rather than blank.
 */
function toRow(item: IClosingDocumentListItem): DocumentRow {
    return {
        id: item.id,
        // C4 — a rename changes the DISPLAYED name and nothing else. `upstreamName` is what
        // SharePoint calls the file today; `filename` is what the file store recorded when the bytes
        // arrived. Both are true, and the one the trustee is looking for is the current one.
        filename: item.upstreamName ?? item.filename,
        account: item.account,
        filing: item.filing,
        assignment: item.assignment,
        ...(item.assignedBy != null ? { assignedBy: item.assignedBy } : {}),
        confidence: item.confidence,
        addedAt: item.addedAt,
        source: item.source,
        notRelevant: item.notRelevant,
        validity: item.validity,
        // C1–C4. Absent means `present`, which is also every manual upload — so the marker is simply
        // not rendered, rather than rendered as "fine".
        ...(item.upstreamState != null ? { upstreamState: item.upstreamState } : {}),
        // ⚠️ C2 — LOUD when the document backs an account, quiet when it does not, and decided HERE.
        // The view must not re-derive "is this assigned?" from the columns it happens to render;
        // two places deciding one thing is how they come to disagree. `assignment === 'assigned'` is
        // the same fact the account column draws, read once.
        ...(item.upstreamState != null && item.upstreamState !== 'present'
            ? { upstreamNoticeIsUrgent: item.assignment === 'assigned' }
            : {}),
        // ID2 — only the NAMES travel; composing the sentence would need i18n in a selector, and the
        // view already falls back to its own label when this is absent.
        ...(item.aliasNames != null && item.aliasNames.length > 0
            ? { aliasHint: item.aliasNames.join(', ') }
            : {}),
        // REV-22010 — a PASS, like `upstreamState`: absent means read, and the view draws nothing.
        ...(item.readingState != null ? { readingState: item.readingState } : {}),
        // REV-22302 — three more passes. The STATE they add up to (working, no proposal, not readable)
        // is derived in the view from a pure presenter, not here: the effect that decides whether to
        // keep polling reads the same presenter over the wire items, so one function answers both.
        ...(item.kind != null ? { kind: item.kind } : {}),
        ...(item.proposalRefusalCode != null ? { proposalRefusalCode: item.proposalRefusalCode } : {}),
        ...(item.proposalRejected === true ? { proposalRejected: true } : {}),
        ...(item.alternativeAccountId != null ? { alternativeAccountId: item.alternativeAccountId } : {}),
        ...(item.proposalSource != null ? { proposalSource: item.proposalSource } : {}),
        ...(item.proposalCertainty != null ? { proposalCertainty: item.proposalCertainty } : {}),
        ...(item.balanceCheck != null ? { balanceCheck: item.balanceCheck } : {}),
        ...(item.processingStep != null ? { processingStep: item.processingStep } : {}),
        ...(item.dateWarning != null ? { dateWarning: item.dateWarning } : {}),
    };
}

/**
 * REV-21869 #164 (A-C3) — the WIRE interpretation → the view's shape, minor units divided by 100
 * EXACTLY ONCE, here. The component only formats an already-converted `amount`; `amountMinor` rides
 * along untouched for the `data-amount-minor` attribute.
 */
function toInterpretation(wire?: IClosingDocumentInterpretation): DocumentInterpretationView | undefined {
    if (wire == null) {
        return undefined;
    }
    const documentDate = wire.documentDate == null ? {} : { documentDate: wire.documentDate };
    if (wire.values != null && wire.values.length > 0) {
        return {
            state: 'value',
            values: wire.values.map((value) => ({
                label: value.label,
                amountMinor: value.amountMinor,
                amount: toUnits(value.amountMinor),
                currency: value.currency,
                ...(value.date == null ? {} : { date: value.date }),
                ...(value.quote == null ? {} : { quote: value.quote }),
            })),
            ...documentDate,
        };
    }
    // REV-22010 — the reason travels as the closed-list identifier; choosing its i18n key is the
    // view's job (a total map), never the store's.
    const reason = wire.notInterpreted?.reason;
    return { state: wire.notInterpreted?.state ?? 'none', ...(reason == null ? {} : { reason }), ...documentDate };
}

function toDetail(item: IClosingDocumentDetail): DocumentDetail {
    const interpretation = toInterpretation(item.interpretation);
    return {
        // `upstreamState`, `upstreamNoticeIsUrgent` and `aliasHint` ride along from the row: the two
        // surfaces must say the same thing about one document, and the loudness rule (C2) is decided
        // exactly once, in `toRow`.
        ...toRow(item),
        previewKind: item.previewKind,
        previewUrl: item.previewUrl,
        // U2 — WHEN it changed, only on this surface: the hub's row has no room for a date, and the
        // single-document page is where the question "since when?" is actually asked.
        ...(item.upstreamStateChangedAt == null
            ? {}
            : { upstreamStateChangedAt: item.upstreamStateChangedAt }),
        ...(interpretation == null ? {} : { interpretation }),
        // REV-22010 — passes, detail only. `readingState` already rode along from `toRow`.
        ...(item.readingQuality == null ? {} : { readingQuality: item.readingQuality }),
        ...(item.proposalRefusal == null ? {} : { proposalRefusal: item.proposalRefusal }),
    };
}

/**
 * The client's half of the search. SORTING IS NO LONGER DONE HERE.
 *
 * The rows arrive already ordered: the server can sort by columns this client cannot reconstruct
 * (`filename` lives on another aggregate, `account` and `assignment` are derived), and a second
 * comparator over the same column is exactly how a table and its own header start disagreeing. The
 * filter below preserves the order it is given, so the server's ordering survives the search.
 *
 * The search is deliberately split between the two sides, because the two halves are different
 * questions:
 *
 *   · the ROWS of the ACTIVE pill — answered HERE, because the client already holds all of them, so
 *     the table narrows on the first keystroke without a round trip;
 *   · the THREE COUNTS — answered by the SERVER, because they span pills the client does not hold.
 *     That is what lets "Bilanz" report Eingang (2) | Zugeordnet (5) | Nicht relevant (0) and the
 *     user click through to where the matches actually are.
 *
 * ⚠️ The predicate below must stay `toLowerCase().includes(...)` over `filename` — it is the SAME
 * expression `DefaultClosingDocumentsService.searchFileIds` runs, and both sides are JavaScript, so
 * case folds identically. Change one and a pill starts reading (3) over a table showing two.
 *
 * ⚠️ Cross-pill ROWS are NOT an option: the hub renders exactly one `activeFilter` and exactly one
 * `aria-current` pill. Showing another pill's matches needs a pure-view change, which belongs to a
 * ticket that owns REV-21367.
 */
const selectVisibleItems = createSelector(selectState, (state) => {
    const term = state.searchTerm.trim().toLowerCase();
    if (term.length === 0) {
        return state.documents;
    }
    // `filter` preserves order, so the rows stay in the order the server sent them.
    return state.documents.filter((item) => item.filename.toLowerCase().includes(term));
});

export const selectDocumentRows = createSelector(selectVisibleItems, (items) => items.map(toRow));
export const selectProjectId = createSelector(selectState, (state) => state.projectId);
export const selectDocumentCounts = createSelector(selectState, (state) => state.counts);
export const selectActiveFilter = createSelector(selectState, (state) => state.activeFilter);
export const selectDocumentSort = createSelector(selectState, (state) => state.sort);
export const selectSearchTerm = createSelector(selectState, (state) => state.searchTerm);
/** REV-21901 N3 (W2) — the "automatisch zugewiesen" facet, read by every reload path that must carry it. */
export const selectAgentOnly = createSelector(selectState, (state) => state.agentOnly);
export const selectDocumentsLoading = createSelector(selectState, (state) => state.loading);
export const selectDocumentsError = createSelector(selectState, (state) => state.error);

/**
 * F5/F9/F10 (#84) — the HUB's shape: the SET of row ids currently writing / that last failed. A
 * `string[]` and not a single id-or-null: the hub can have MORE THAN ONE row in flight or failed at
 * once, and a single slot was a real double-write path (Sicherheitspruefung, Fall 18) — accepting a
 * SECOND document silently "freed" the first one's row while its write was still running. Every row
 * checks its OWN id against these sets, exactly like every other per-row input.
 */
export const selectProposalPendingIds = createSelector(selectState, (state) => state.proposalPendingIds);
export const selectProposalFailedIds = createSelector(selectState, (state) => state.proposalFailedIds);
/** REV-22366 — the files of a hub upload that the list does not hold yet. */
export const selectUploadingFilenames = createSelector(selectState, (state) => state.uploadingFilenames);
/** REV-22366 — the rows with a write running or waiting for the list that shows its result. */
export const selectPendingRowIds = createSelector(selectState, (state) => [
    ...state.rowWritesRunning,
    ...state.rowWritesAwaitingList,
]);

export const selectDocumentDetail = createSelector(selectState, (state) =>
    state.document == null ? null : toDetail(state.document),
);
export const selectDocumentDetailLoading = createSelector(selectState, (state) => state.documentLoading);
export const selectDocumentDetailError = createSelector(selectState, (state) => state.documentError);

/**
 * F5 (#84) — the SINGLE-DOCUMENT page's shape: a boolean, because this surface shows exactly one
 * document. `true` only while the open document's id is IN the pending/failed set — a write that
 * started on a document the user has since navigated away from must not paint stale banners on
 * whatever they opened next.
 */
export const selectDocumentProposalPending = createSelector(
    selectState,
    (state) => state.document != null && state.proposalPendingIds.includes(state.document.id),
);
export const selectDocumentProposalFailed = createSelector(
    selectState,
    (state) => state.document != null && state.proposalFailedIds.includes(state.document.id),
);

/** The document page: a write for the open document is running (any of its actions). */
export const selectDocumentWriting = createSelector(
    selectState,
    (state) =>
        state.document != null &&
        (state.rowWritesRunning.includes(state.document.id) || state.proposalPendingIds.includes(state.document.id)),
);

/** The document page: the last write for the open document failed (any of its actions). */
export const selectDocumentWriteFailed = createSelector(
    selectState,
    (state) =>
        state.document != null &&
        (state.documentWriteFailedIds.includes(state.document.id) || state.proposalFailedIds.includes(state.document.id)),
);

/**
 * The picker's account list. A PASS, not a map: the wire shape and `AccountOption` are the same by
 * design, so nothing here can drift from what the server sends.
 */
export const selectAccounts = createSelector(selectState, (state) => state.accounts);
/** How far the plan read has got. `'loaded'` is what lets the guard skip a request. */
export const selectAccountsStatus = createSelector(selectState, (state) => state.accountsStatus);

/** The open account's documents. Not filtered here — the SERVER already answered for this account. */
export const selectAccountDocumentRows = createSelector(selectState, (state) =>
    state.accountDocuments.map(toRow),
);
/** Loading only while there is nothing to show yet — a re-read updates the list in place. */
export const selectAccountDocumentsLoading = createSelector(
    selectState,
    (state) => state.accountDocumentsLoading && state.accountDocuments.length === 0,
);
export const selectAccountDocumentsError = createSelector(selectState, (state) => state.accountDocumentsError);
/** Which account's documents were asked for last, in which closing — `null` before any. */
export const selectAccountDocumentsRequest = createSelector(selectState, (state) =>
    state.projectId === null || state.accountDocumentsAccountId === null
        ? null
        : { projectId: state.projectId, accountId: state.accountDocumentsAccountId },
);

export const selectUploadPending = createSelector(selectState, (state) => state.uploadPending);
export const selectUploadFailures = createSelector(selectState, (state) => state.uploadFailures);
/**
 * One selector for both, because the settle action changes them together: two separate selects can
 * emit "no longer pending" before the failures arrive, and a caller would close over an error.
 */
export const selectUploadState = createSelector(
    selectUploadPending,
    selectUploadFailures,
    (pending, failures): IUploadState => ({ pending, failures }),
);

/** The post-upload poll's watch-list (#162) — non-empty while `pollAfterUpload$` is still asking. */
export const selectPendingUploadFilenames = createSelector(selectState, (state) => state.pendingUploadFilenames);
export const selectUploadPollTick = createSelector(selectState, (state) => state.uploadPollTick);

export const selectSyncPending = createSelector(selectState, (state) => state.syncPending);
export const selectLastSync = createSelector(selectState, (state) => state.lastSync);

// --- REV-21964 - R1/R3/U1: der Laufbericht -------------------------------------------------------

/**
 * Wire run -> the hub's report. A RENAME and a FLATTENING, never a translation.
 *
 * ⚠️ The per-file REASONS travel as identifiers. Choosing the i18n key for `tooLarge` is
 * presentation and belongs in the view, which does it with a TOTAL map over the closed union — a
 * selector composing the sentence would put translation in the store, and the same skip would end
 * up worded one way here and another way on whatever surface shows it next.
 *
 * The one thing this DOES decide is which bindings count as failures: `state === 'failed'`, and the
 * COUNT stays the server's (`counts.failedFolders`). A folder we could not read is not a file we
 * skipped — the first hides an unknown number of documents, the second is a decision we made and
 * can name — so the two are never merged into one list (R1).
 */
function toFolderFailures(run: ISyncRun): SyncFolderFailure[] {
    return run.bindings
        .filter((binding) => binding.state === 'failed')
        .map((binding) => ({
            folderPath: binding.folderPath,
            ...(binding.error == null ? {} : { message: binding.error }),
        }));
}

function toSyncReport(run: ISyncRun): SyncReportView {
    return {
        status: run.status,
        startedAt: run.startedAt,
        ...(run.finishedAt == null ? {} : { finishedAt: run.finishedAt }),
        discovered: run.counts.discovered,
        created: run.counts.created,
        skipped: run.counts.skipped,
        failedFolders: run.counts.failedFolders,
        // Passed through, not re-counted: `skips.length` is the LIST, `counts.skipped` is the TRUTH,
        // and they differ exactly when the list was cut. Deriving the count from the list is how a
        // truncation becomes invisible (ADR-0009 §12).
        skips: run.skips.map((skip) => ({ filename: skip.filename, reason: skip.reason })),
        skipsTruncated: run.skipsTruncated,
        failures: toFolderFailures(run),
    };
}

export const selectSyncReport = createSelector(selectState, (state) =>
    state.syncRun == null ? null : toSyncReport(state.syncRun),
);
/** R3/R6 — why the last start was refused. `null` while the last one was accepted. */
export const selectSyncRefusal = createSelector(selectState, (state) => state.syncRefusal);
/** The poll's own ceiling counter — read by the effect, never rendered. */
export const selectSyncPollTick = createSelector(selectState, (state) => state.syncPollTick);
